import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, UserCheck, IndianRupee, CalendarOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardData } from '../hooks/useDashboardData';
import PageHeader from '../components/PageHeader';
import WelcomeHeader from '../components/dashboard/WelcomeHeader';
import ActionStrip from '../components/dashboard/ActionStrip';
import StatCard from '../components/dashboard/StatCard';
import SchedulerLinkCard from '../components/dashboard/SchedulerLinkCard';
import OnboardingChecklist from '../components/dashboard/OnboardingChecklist';
import TrialBanner from '../components/dashboard/TrialBanner';
import { formatCurrency } from '../utils/format';
import { formatHour } from '../utils/formatTime';

const PageSpinner = () => (
  <svg className="w-6 h-6 animate-spin text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function DashboardPage() {
  const auth = useAuth();
  const { t } = useTranslation('common');
  const { data, loading, error, refetch } = useDashboardData();

  const shiftsToday = data?.shiftsToday;
  const costThisMonth = data?.costThisMonth;

  let shiftsSubtitle = null;
  if (shiftsToday?.nextShiftStartTime) {
    shiftsSubtitle = t('dashboard.stats.shiftsToday.next', {
      time: formatHour(shiftsToday.nextShiftStartTime),
      name: shiftsToday.nextShiftEmployeeFirstName ?? '',
    });
  } else if (shiftsToday?.total === 0) {
    shiftsSubtitle = t('dashboard.stats.shiftsToday.none');
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <PageHeader title={t('dashboard.title')} />

      <div className="px-6 py-8 max-w-4xl mx-auto w-full">
        <WelcomeHeader fullName={auth.fullName} trial={data?.trial} />

        {loading && !data && (
          <div className="flex items-center justify-center py-16">
            <PageSpinner />
          </div>
        )}

        {error && !data && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-center justify-between gap-4">
            <span>{t('dashboard.error')}</span>
            <button
              type="button"
              onClick={refetch}
              className="font-semibold underline underline-offset-2 hover:no-underline"
            >
              {t('dashboard.retry')}
            </button>
          </div>
        )}

        {data && (
          <>
            <ActionStrip alerts={data.alerts} />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={<Clock size={16} />}
                label={t('dashboard.stats.shiftsToday.label')}
                value={shiftsToday?.total ?? 0}
                subtitle={shiftsSubtitle}
              />
              <StatCard
                icon={<UserCheck size={16} />}
                label={t('dashboard.stats.punchedIn.label')}
                value={t('dashboard.stats.punchedIn.placeholder')}
                subtitle={t('dashboard.stats.punchedIn.hint')}
              />
              <StatCard
                icon={<IndianRupee size={16} />}
                label={t('dashboard.stats.costThisMonth.label')}
                value={formatCurrency((costThisMonth?.amountPaise ?? 0) / 100)}
                subtitle={
                  costThisMonth
                    ? t('dashboard.stats.costThisMonth.progress', {
                        day: costThisMonth.dayOfMonth,
                        total: costThisMonth.totalDaysInMonth,
                      })
                    : null
                }
              />
              <StatCard
                icon={<CalendarOff size={16} />}
                label={t('dashboard.stats.leavesToday.label')}
                value={t('dashboard.stats.leavesToday.placeholder')}
                subtitle={t('dashboard.stats.leavesToday.hint')}
              />
            </div>

            <SchedulerLinkCard />

            <OnboardingChecklist onboarding={data.onboarding} />

            <TrialBanner trial={data.trial} />
          </>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;